import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import type { AppMessages } from "./messages/zh-CN";
import { applyDocumentLocale, SUPPORTED_LOCALES } from "./resolveLocale";
import {
  catalogFor,
  changeRuntimeLocale,
  getRuntimeLocale,
  initRuntimeLocale,
  subscribeRuntimeLocale,
} from "./runtime";
import { translate } from "./translate";
import type { AppLocale, TranslateFn } from "./types";

export interface I18nContextValue {
  locale: AppLocale;
  locales: readonly AppLocale[];
  setLocale: (locale: AppLocale) => void;
  t: TranslateFn<AppMessages>;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** 订阅运行时语言，组件在语言切换时立即重渲染。 */
function useLocaleValue(): I18nContextValue {
  const locale = useSyncExternalStore(subscribeRuntimeLocale, getRuntimeLocale, getRuntimeLocale);
  const t = useCallback<TranslateFn<AppMessages>>(
    (key, vars) => translate(catalogFor(locale), String(key), vars),
    [locale],
  );
  return useMemo(
    () => ({ locale, locales: SUPPORTED_LOCALES, setLocale: changeRuntimeLocale, t }),
    [locale, t],
  );
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  // 首次渲染前解析语言，避免登录页先闪一帧默认语言。
  useMemo(() => initRuntimeLocale(), []);
  const value = useLocaleValue();
  useEffect(() => { applyDocumentLocale(value.locale); }, [value.locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * 读取当前语言与翻译函数。
 *
 * 没有 `LocaleProvider` 时回退到运行时 store（默认 zh-CN），
 * 这样单测里直接 `render(<Page />)` 也能工作。
 */
export function useI18n(): I18nContextValue {
  const fallback = useLocaleValue();
  const context = useContext(I18nContext);
  return context ?? fallback;
}

/** `useI18n()` 的别名，只关心翻译函数时用。 */
export function useTranslation(): I18nContextValue {
  return useI18n();
}
